import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import {
  TEXT_FONT_FAMILY,
  sanitizeRichText,
  textAlignCss,
  textFontWeight,
  textShadowCss,
  textVerticalAlignCss,
  textWidgetStyle,
} from '@akarya/asp-client';
import { api } from '../../lib/api.js';
import { RESIZE_HANDLES, dragRect, snapRect, type DragMode, type SnapGuides } from '../../lib/regionGeometry.js';
import type { LayoutDocument, LayoutRegion, MediaItem } from '../../lib/types.js';

/** How far (screen px) a pointer must travel before a press on a region becomes a move, so a tap only selects. */
const DRAG_THRESHOLD = 4;
/** How close (screen px) an edge must come to a guide line to snap onto it. */
const SNAP_DISTANCE = 6;

const CORNERS: Exclude<DragMode, 'move'>[] = ['nw', 'ne', 'se', 'sw'];

/** dataTransfer types for dragging a widget type (from the tiles) or an existing playlist item. */
export const DND_MODULE = 'application/x-akarya-module';
export const DND_WIDGET = 'application/x-akarya-widget';

/**
 * Scaled, interactive view of the layout: regions can be dragged to move,
 * resized by their handles, and nudged with the arrow keys (Shift = 10px).
 * Drags snap to the canvas and to other regions (hold Alt to place freely).
 *
 * The canvas is fitted inside its frame (which needs a height from its
 * parent) and then multiplied by `zoom`; above 1 the frame scrolls.
 */
export function LayoutCanvas({
  doc,
  media,
  selectedId,
  onSelect,
  onRectChange,
  onGestureStart,
  onGestureEnd,
  zoom = 1,
  onDropData,
}: {
  doc: LayoutDocument;
  media: MediaItem[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** `key` groups repeated changes (e.g. arrow-key nudges) into one undo step. */
  onRectChange: (regionIndex: number, rect: LayoutRegion['rect'], key?: string) => void;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
  /** A multiple of the fitted size. */
  zoom?: number;
  /** Accepts a drag (a widget tile or playlist row) dropped onto a region. */
  onDropData?: (regionIndex: number, data: DataTransfer) => void;
}): ReactNode {
  const { width, height } = doc.dimensions;
  const mediaBySha = useMemo(() => new Map(media.map((m) => [m.sha256, m])), [media]);
  const frame = useRef<HTMLDivElement>(null);
  const [frameSize, setFrameSize] = useState({ width: 320, height: 320 });
  const [guides, setGuides] = useState<SnapGuides | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const drag = useRef<{
    index: number;
    mode: DragMode;
    startX: number;
    startY: number;
    rect: LayoutRegion['rect'];
    active: boolean;
  } | null>(null);

  useEffect(() => {
    const el = frame.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setFrameSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scale =
    Math.min(Math.max(frameSize.width, 60) / Math.max(width, 1), Math.max(frameSize.height, 60) / Math.max(height, 1)) *
    zoom;

  function startDrag(e: PointerEvent<HTMLElement>, index: number, mode: DragMode): void {
    if (e.button !== 0) return;
    const region = doc.regions[index];
    if (!region) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    // preventDefault() suppresses the click-to-focus, so focus by hand to keep
    // the arrow-key nudge working after a click.
    e.currentTarget.closest<HTMLElement>('.preview-region')?.focus({ preventScroll: true });
    onSelect(region.id);
    // A resize handle acts at once; a move waits for real travel.
    const active = mode !== 'move';
    if (active) onGestureStart?.();
    drag.current = { index, mode, startX: e.clientX, startY: e.clientY, rect: region.rect, active };
  }

  function moveDrag(e: PointerEvent<HTMLElement>): void {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.active) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      d.active = true;
      onGestureStart?.();
    }
    const raw = dragRect(d.rect, d.mode, dx / scale, dy / scale, doc.dimensions);
    if (e.altKey) {
      setGuides(null);
      onRectChange(d.index, raw);
      return;
    }
    const others = doc.regions.filter((_, i) => i !== d.index).map((r) => r.rect);
    const snapped = snapRect(raw, d.mode, others, doc.dimensions, SNAP_DISTANCE / scale);
    setGuides(snapped.guides.x.length || snapped.guides.y.length ? snapped.guides : null);
    onRectChange(d.index, snapped.rect);
  }

  function endDrag(): void {
    const d = drag.current;
    drag.current = null;
    setGuides(null);
    if (d?.active) onGestureEnd?.();
  }

  function nudge(e: KeyboardEvent<HTMLElement>, index: number): void {
    const step = e.shiftKey ? 10 : 1;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = delta[e.key];
    const region = doc.regions[index];
    if (!d || !region) return;
    e.preventDefault();
    onRectChange(index, dragRect(region.rect, 'move', d[0], d[1], doc.dimensions), `nudge:${region.id}`);
  }

  return (
    <div
      ref={frame}
      className="canvas-fit"
      onPointerDown={() => onSelect(null)}
    >
      <div
        className="preview-canvas"
        style={{ width: width * scale, height: Math.max(height * scale, 40) }}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {doc.regions.map((r, ri) => {
          const percent = r.rect.unit === 'percent';
          const selected = r.id === selectedId;
          const screenW = (percent ? (r.rect.width / 100) * width : r.rect.width) * scale;
          const screenH = (percent ? (r.rect.height / 100) * height : r.rect.height) * scale;
          // Small regions get fewer handles so there is still room to grab the region itself.
          const short = Math.min(screenW, screenH);
          const handles = short < 40 ? (['se'] as const) : short < 72 ? CORNERS : RESIZE_HANDLES;
          return (
            <div
              key={r.id}
              className={`preview-region draggable${selected ? ' selected' : ''}${dropTarget === r.id ? ' drop-target' : ''}`}
              tabIndex={0}
              role="button"
              aria-label={`Region ${r.name ?? r.id}`}
              style={{
                left: percent ? `${r.rect.x}%` : r.rect.x * scale,
                top: percent ? `${r.rect.y}%` : r.rect.y * scale,
                width: percent ? `${r.rect.width}%` : r.rect.width * scale,
                height: percent ? `${r.rect.height}%` : r.rect.height * scale,
                zIndex: r.zIndex ?? 0,
              }}
              onPointerDown={(e) => startDrag(e, ri, 'move')}
              onFocus={() => onSelect(r.id)}
              onKeyDown={(e) => nudge(e, ri)}
              onDragOver={
                onDropData
                  ? (e) => {
                      const { types } = e.dataTransfer;
                      if (!types.includes(DND_MODULE) && !types.includes(DND_WIDGET)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = types.includes(DND_MODULE) ? 'copy' : 'move';
                      if (dropTarget !== r.id) setDropTarget(r.id);
                    }
                  : undefined
              }
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropTarget(null);
              }}
              onDrop={
                onDropData
                  ? (e) => {
                      e.preventDefault();
                      setDropTarget(null);
                      onDropData(ri, e.dataTransfer);
                    }
                  : undefined
              }
            >
              <RegionPreview region={r} doc={doc} scale={scale} mediaBySha={mediaBySha} />
              <span className="preview-region-label">
                {r.name ?? r.id}
                <br />
                {r.playlist.items.length} item{r.playlist.items.length === 1 ? '' : 's'}
              </span>
              {selected
                ? handles.map((h) => (
                    <span key={h} className={`resize-handle ${h}`} onPointerDown={(e) => startDrag(e, ri, h)} />
                  ))
                : null}
            </div>
          );
        })}
        {guides?.x.map((x) => <span key={`x${x}`} className="snap-guide v" style={{ left: x * scale }} />)}
        {guides?.y.map((y) => <span key={`y${y}`} className="snap-guide h" style={{ top: y * scale }} />)}
      </div>
    </div>
  );
}

/** A static approximation of what the player shows first in a region: the
 * first enabled playlist item, styled like the runtime's native widgets
 * (players/runtime/src/render/widget.ts). Not interactive, and custom modules
 * only get a placeholder. */
function RegionPreview({
  region,
  doc,
  scale,
  mediaBySha,
}: {
  region: LayoutRegion;
  doc: LayoutDocument;
  scale: number;
  mediaBySha: Map<string, MediaItem>;
}): ReactNode {
  const widget = region.playlist.items.find((w) => w.enabled !== false);
  if (!widget) return null;

  const options = widget.options ?? {};
  const color = typeof options.color === 'string' ? options.color : '#fff';
  // The runtime sizes text relative to the layout: 1% of its width, or (for a portable CMS text
  // size) 1/540 of its short side per sp.
  const vw = (doc.dimensions.width * scale) / 100;
  const sp = (Math.min(doc.dimensions.width, doc.dimensions.height) * scale) / 540;
  let body: ReactNode;

  switch (widget.module) {
    case 'image':
    case 'video': {
      const sha = widget.media?.find((m) => (m.role ?? 'primary') === 'primary')?.sha256 ?? widget.media?.[0]?.sha256;
      const item = sha ? mediaBySha.get(sha) : undefined;
      if (!item) body = <span className="preview-placeholder">{widget.module}: no media</span>;
      else if (widget.module === 'image') body = <img className="preview-fill" src={api.assetUrl(item.url)} alt="" />;
      else body = <video className="preview-fill" src={api.assetUrl(item.url)} muted playsInline preload="metadata" />;
      break;
    }
    case 'text': {
      const text = textWidgetStyle(options);
      const html = typeof options.html === 'string' && options.html ? options.html : '';
      body = (
        <div
          className={text.running ? 'preview-fill preview-text preview-running' : 'preview-fill preview-text'}
          style={{
            color,
            background: typeof options.backgroundColor === 'string' ? options.backgroundColor : 'transparent',
            fontSize: text.sizeSp === undefined ? 4 * vw : text.sizeSp * sp,
            fontWeight: textFontWeight(text, Boolean(html)),
            fontStyle: text.italic ? 'italic' : undefined,
            textDecoration: text.underline ? 'underline' : undefined,
            textShadow: text.shadow ? textShadowCss(color) : undefined,
            fontFamily: TEXT_FONT_FAMILY[text.font],
            lineHeight: text.lineSpacingPercent / 100,
            // Running text previews as its first line, entering from the left like on the portable CMS's canvas.
            alignItems: text.running ? 'center' : textVerticalAlignCss(text.verticalAlign),
            textAlign: text.running ? 'left' : textAlignCss(text.align),
          }}
        >
          {html ? (
            // Same sanitizer and structure as the player's text widget.
            <div className="preview-rich" dangerouslySetInnerHTML={{ __html: sanitizeRichText(html) }} />
          ) : (
            <div className="preview-plain">{typeof options.text === 'string' ? options.text : ''}</div>
          )}
        </div>
      );
      break;
    }
    case 'clock':
      body = <ClockPreview format={options.format} timeZone={options.timeZone} color={color} fontSize={8 * vw} />;
      break;
    case 'web': {
      const url = webPreviewUrl(options.url);
      if (!url) {
        body = <span className="preview-placeholder">web: no URL</span>;
        break;
      }
      // Render the page at the region's real pixel size, then scale it down.
      const percent = region.rect.unit === 'percent';
      const w = percent ? (region.rect.width / 100) * doc.dimensions.width : region.rect.width;
      const h = percent ? (region.rect.height / 100) * doc.dimensions.height : region.rect.height;
      body = (
        <iframe
          className="preview-web"
          src={url}
          title="web preview"
          sandbox=""
          tabIndex={-1}
          style={{ width: w, height: h, transform: `scale(${scale})` }}
        />
      );
      break;
    }
    default:
      body = <span className="preview-placeholder">{widget.module}</span>;
  }

  return <div className="preview-content">{body}</div>;
}

function webPreviewUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function ClockPreview({
  format,
  timeZone,
  color,
  fontSize,
}: {
  format: unknown;
  timeZone: unknown;
  color: string;
  fontSize: number;
}): ReactNode {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const hour12 = (typeof format === 'string' ? format : 'HH:mm').toLowerCase().includes('a');
  let text: string;
  try {
    text = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12,
      timeZone: typeof timeZone === 'string' && timeZone ? timeZone : undefined,
    }).format(now);
  } catch {
    text = 'invalid time zone';
  }

  return (
    <div className="preview-fill preview-clock" style={{ color, fontSize }}>
      {text}
    </div>
  );
}
